import classNames from 'classnames'
import moment from 'moment-timezone'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useManagementGetCompanies } from '@apps-common/apis/management/company'
import { useManagementGetCompanyUnitsReport } from '@apps-common/apis/management/reporting'
import { ButtonRoundedWithIcon } from '@apps-common/components/elements'
import { CheckboxInputSM } from '@apps-common/components/elements/form/CheckboxInputSM'
import { DatePickerInputSm } from '@apps-common/components/elements/form/DatepickerInputSM'
import { SelectInputSM } from '@apps-common/components/elements/form/SelectInputSM'
import { VertSpacerLg, VertSpacerMd, VertSpacerSm } from '@apps-common/components/layout'
import { KeyValueCheckboxModal } from '@apps-common/components/modals'
import { MESSAGE_GENERIC_ERROR } from '@apps-common/config/app-messages'
import { commonIcons } from '@apps-common/config/icons'
import { INPUT_DATEPICKER_CLASSES_SM, INPUT_DATEPICKER_CONTAINER_SM } from '@apps-common/config/input-styles'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { toastError, toastInfo, toastWarning } from '@apps-common/lib/toast'
import { ISelectOption } from '@apps-common/types/form'
import { ICompanyListModel } from '@apps-common/types/management/company'
import { IReportCompanyUnitParamsModel, ReportCompanyType } from '@apps-common/types/management/reporting'
import { useIonModal } from '@ionic/react'
import { motion } from 'framer-motion'
import { createAndDownloadExcel, getAvailablePropertiesForUnitsReport } from './generate-xlsx'

export const ReportingUnits = () => {
  const [reportRunning, setReportRunning] = useState<boolean>(false)
  const [cancelReport, setCancelReport] = useState<boolean>(false)
  const cancelReportRef = useRef<boolean>(false)

  const { isLoading: companiesIsLoading, isError: companiesIsError, data: companiesData } = useManagementGetCompanies({ name: '' })
  const { mutateAsync: getReportsAsync, isPending: getReportIsPending } = useManagementGetCompanyUnitsReport(false, () => {})

  useEffect(() => {
    cancelReportRef.current = cancelReport
  }, [cancelReport])

  const runReport = async (params: IReportCompanyUnitParamsModel, includedProperties: ISelectOption[]) => {
    let companies: ICompanyListModel[] = []

    if (!companiesData || companiesData.length === 0) {
      toastError('No companies loaded. Cannot run report.')
      return
    }

    if (params.reportCompanyType === ReportCompanyType.allCompanies) {
      params.ignoreChargeDirect = false
    }

    if (params.companyId && params.reportCompanyType === ReportCompanyType.individualCompany) {
      const company = companiesData?.find((cy) => cy.companyId === params.companyId)
      if (company) {
        companies.push(company)
      } else {
        toastError('Selected company not found. Select another company and try again.')
        return
      }
    } else if (params.reportCompanyType === ReportCompanyType.allChargeDirect) {
      companies = companiesData.filter((cy) => cy.chargeDirect === true)
    } else if (params.reportCompanyType === ReportCompanyType.allCompanies) {
      companies = companiesData
    }

    setReportRunning(() => true)

    try {
      for (let i = 0; i < companies.length; i++) {
        const company = companies[i]

        if (cancelReportRef.current === true) {
          toastInfo('Report cancelled.', 3000)
          setReportRunning(() => false)
          setCancelReport(() => false)
          break
        }

        const data = await getReportsAsync({ model: { ...params, companyId: company.companyId } })

        const noUnitData = data.every((group) => group.data.length === 0)

        if (noUnitData && params.reportCompanyType !== ReportCompanyType.individualCompany) {
          toastInfo(`Skipping download of report for company: ${company.companyName}. No devices in report`, 3000)
        } else {
          toastInfo(`Downloading report for company: ${company.companyName}`, 3000)
          createAndDownloadExcel(data, company.companyName, params.startDate, includedProperties)
        }
      }
    } catch (e: any) {
      toastError('An error occurred while running the report. Please try again.')
      if (e?.message) {
        toastError(e.message)
      }
    }

    setReportRunning(false)
  }

  const {
    register,
    setValue,
    getValues,
    watch,
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<IReportCompanyUnitParamsModel>({
    defaultValues: {
      new: true,
      inactive: true,
      active: true,
      cancelled: true,
      companyId: '',
      reportCompanyType: ReportCompanyType.allChargeDirect,
      startDate: moment.utc().startOf('month').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISOString(),
      endDate: moment.utc().endOf('month').set({ hour: 23, minute: 59, second: 59, millisecond: 999 }).toISOString(),
      cancelledStartDate: moment
        .utc()
        .startOf('month')
        .add(6, 'day')
        .set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
        .toISOString()
    }
  })

  const companyIdVal = watch('companyId')
  const cancelledVal = watch('cancelled')
  const reportCompanyTypeVal = watch('reportCompanyType')

  const getSelectedCompany = () => {
    if (companyIdVal && companyIdVal.length > 0) {
      return companiesData?.find((cy) => cy.companyId === companyIdVal)
    }

    return null
  }

  useEffect(() => {
    setValue('ignoreChargeDirect', false)
  }, [companyIdVal])

  useEffect(() => {
    if (reportCompanyTypeVal !== ReportCompanyType.individualCompany) {
      setValue('companyId', '', { shouldValidate: true })
    }
  }, [reportCompanyTypeVal])

  const [present, dismiss] = useIonModal(KeyValueCheckboxModal, {
    componentProps: {
      keyValues: getAvailablePropertiesForUnitsReport()
    },
    onDismiss: (data: string, role: string) => {
      if (role === ModalDismiss.Confirm) {
        const selectedProps = JSON.parse(data) as ISelectOption[]
        if (selectedProps && selectedProps.length > 0) {
          const formValues = getValues()
          runReport(formValues, selectedProps)
        } else {
          toastWarning('No properties selected. Please select at least one property to include in the report.')
        }
      }

      dismiss()
    }
  })

  const onSubmit = async () => {
    try {
      present()
    } catch (e: any) {
      toastError(MESSAGE_GENERIC_ERROR)
    }
  }

  const loading = companiesIsLoading || companiesIsError
  const updating = isSubmitting || getReportIsPending

  return (
    <div>
      <form noValidate id='companyModalForm' onSubmit={handleSubmit(onSubmit)}>
        <div className='w-full'>
          <div className='w-full'>
            <CheckboxInputSM
              label='New devices'
              id='newUnits'
              containerClassName='grow'
              disabled={loading}
              registration={register('new')}
              errors={errors.new}
            />
            <CheckboxInputSM
              label='Inactive devices'
              id='activeUnits'
              containerClassName='grow'
              disabled={loading}
              registration={register('inactive')}
              errors={errors.inactive}
            />
            <CheckboxInputSM
              label='Active devices'
              id='activeUnits'
              containerClassName='grow'
              disabled={loading}
              registration={register('active')}
              errors={errors.active}
            />
            <CheckboxInputSM
              label='Cancelled devices'
              id='cancelledUnits'
              containerClassName='grow'
              disabled={loading}
              registration={register('cancelled')}
              errors={errors.cancelled}
            />
          </div>
          <VertSpacerLg />
          <SelectInputSM
            type='text'
            placeholder='Select report type'
            label='Report type'
            id='reportCompanyType'
            showLabel
            disabled={loading}
            registration={register('reportCompanyType', {
              required: {
                value: true,
                message: 'Required'
              }
            })}
            options={[
              { label: 'All charge direct companies', value: ReportCompanyType.allChargeDirect },
              { label: 'All companies', value: ReportCompanyType.allCompanies },
              { label: 'Individual company', value: ReportCompanyType.individualCompany }
            ]}
            errors={errors.reportCompanyType}
          />
          <VertSpacerSm />
          {reportCompanyTypeVal === ReportCompanyType.individualCompany && (
            <>
              <motion.div key='reporting-units-select-company' {...motionFadeIn(0.25)}>
                <SelectInputSM
                  type='text'
                  placeholder='Select a company to report'
                  label='Companies'
                  id='companyId'
                  showLabel
                  disabled={loading || reportCompanyTypeVal !== ReportCompanyType.individualCompany}
                  registration={register('companyId', {
                    required: {
                      value: reportCompanyTypeVal === ReportCompanyType.individualCompany,
                      message: 'Required'
                    }
                  })}
                  options={
                    companiesData?.map((cy) => ({
                      label: `${cy.companyName}${cy.chargeDirect ? ' - Charge Direct' : ''}`,
                      value: cy.companyId
                    })) || []
                  }
                  errors={errors.companyId}
                />
              </motion.div>
              {companyIdVal && companyIdVal.length > 0 && (
                <motion.div key='reporting-units-selected-company-ignore-charger-direct' {...motionFadeIn(0.25)}>
                  <div className='bg-surface-2 border border-border rounded-3xl p-4'>
                    <CheckboxInputSM
                      label={`Override charge direct setting for ${getSelectedCompany()?.companyName || 'selected company'}`}
                      id='ignoreChargeDirect'
                      info='If checked, the report will include all units for this company, regardless of charge direct status.'
                      disabled={loading}
                      registration={register('ignoreChargeDirect')}
                      errors={errors.ignoreChargeDirect}
                    />
                  </div>
                </motion.div>
              )}
            </>
          )}
          <VertSpacerSm />
          <div className='flex items-center justify-start flex-wrap gap-x-[10px]'>
            <DatePickerInputSm
              control={control}
              id='startDate'
              name='startDate'
              containerClassName={classNames(INPUT_DATEPICKER_CONTAINER_SM, 'w-full sm:w-auto grow')}
              className={classNames(INPUT_DATEPICKER_CLASSES_SM)}
              placeholder='Start date'
              label='Start date'
              showLabel
              errors={errors.startDate}
              disabled={loading}
              offset='startOfDay'
            />
            <DatePickerInputSm
              control={control}
              id='endDate'
              name='endDate'
              containerClassName={classNames(INPUT_DATEPICKER_CONTAINER_SM, 'w-full sm:w-auto grow')}
              className={INPUT_DATEPICKER_CLASSES_SM}
              placeholder='End date'
              label='End date'
              showLabel
              errors={errors.startDate}
              disabled={loading}
              offset='endOfDay'
            />
            {cancelledVal && (
              <DatePickerInputSm
                control={control}
                id='cancelledStartDate'
                name='cancelledStartDate'
                containerClassName={classNames(INPUT_DATEPICKER_CONTAINER_SM, 'w-full sm:w-auto grow')}
                className={INPUT_DATEPICKER_CLASSES_SM}
                placeholder='Cancelled start date'
                label='Cancelled start date'
                showLabel
                errors={errors.cancelledStartDate}
                disabled={loading}
                offset='endOfDay'
              />
            )}
          </div>
        </div>
        <VertSpacerMd />
        <div className='flex items-center justify-end'>
          <div className='flex items-center justify-between gap-[10px]'>
            {reportRunning && !cancelReport && (
              <motion.div key='reporting-units-cancel-report-button' {...motionFadeIn(0.25)}>
                <ButtonRoundedWithIcon
                  type='button'
                  text='Cancel report'
                  icon={<commonIcons.close />}
                  className='action-warning'
                  iconContainerClassName='bg-warning text-on-warning'
                  callback={() => setCancelReport(() => true)}
                />
              </motion.div>
            )}
            <ButtonRoundedWithIcon
              type='submit'
              text='Run report'
              icon={<commonIcons.arrowRight />}
              disabled={loading || updating}
              className='action-success'
              iconContainerClassName='bg-success text-on-success'
            />
          </div>
        </div>
      </form>
    </div>
  )
}
